import { API } from "../../EndpPoints";
import { getAuth } from "firebase/auth";

const STORAGE_KEY = "resumeiq:subscription-access:v1";
const SYNC_STORAGE_KEY = "resumeiq:subscription-sync:v1";

const SUCCESS_STATUSES = new Set([
  "success",
  "paid",
  "captured",
  "authorized",
]);

function getStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch (error) {
    console.error("Unable to access local storage for payment access.", error);
    return null;
  }
}

function sanitizeSegment(value, fallback = "unknown") {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || fallback;
}

function readStoreMap() {
  const storage = getStorage();
  if (!storage) return {};

  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Unable to parse paid download access store.", error);
    return {};
  }
}

function writeStoreMap(nextMap) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(nextMap));
  } catch (error) {
    console.error("Unable to save paid download access store.", error);
  }
}

function broadcastSubscriptionUpdate(subscription) {
  if (typeof window === "undefined" || !window.dispatchEvent) return;

  try {
    const storage = getStorage();
    if (storage) {
      storage.setItem(
        SYNC_STORAGE_KEY,
        JSON.stringify({
          updatedAt: Date.now(),
          subscription: subscription || null,
        }),
      );
    }

    window.dispatchEvent(
      new CustomEvent("resumeiq:subscription-updated", {
        detail: {
          subscription: subscription || null,
          updatedAt: Date.now(),
        },
      }),
    );
  } catch (error) {
    console.error("Unable to broadcast subscription update.", error);
  }
}

function safeJsonParse(text) {
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch (error) {
    return null;
  }
}

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .toLowerCase();
}

function isSuccessfulStatus(status) {
  return SUCCESS_STATUSES.has(normalizeStatus(status));
}

function buildStatusPayload(context = {}) {
  return {
    userName: context.userName || "",
    source: context.source || "",
  };
}

export async function getAuthHeaders(extraHeaders = {}, forceRefresh = false) {
  const user = getAuth().currentUser;
  if (!user) {
    throw new Error("You must be signed in to use premium features.");
  }

  const token = await user.getIdToken(forceRefresh);
  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

export async function authenticatedFetch(url, options = {}) {
  const request = async (forceRefresh = false) =>
    fetch(url, {
      ...options,
      headers: await getAuthHeaders(options.headers || {}, forceRefresh),
    });

  const response = await request();
  if (response.status !== 401) {
    return response;
  }

  return request(true);
}

async function parseStatusResponse(response) {
  const text = await response.text();
  const data = safeJsonParse(text);

  if (!response.ok) {
    const error = new Error(
      data?.message || data?.error || text || "Payment status request failed.",
    );
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

function extractPaymentEligibility(data) {
  const subscription = data?.subscription || data?.record?.subscription || null;
  const rawStatus =
    subscription?.status ||
    data?.status ||
    data?.paymentStatus ||
    data?.payment?.status ||
    data?.record?.status ||
    data?.paymentRecord?.status ||
    "";
  const normalized = normalizeStatus(rawStatus);
  const success = Boolean(subscription?.active) || isSuccessfulStatus(normalized);
  const downloadEligible =
    typeof subscription?.active === "boolean"
      ? subscription.active
      : typeof data?.downloadEligible === "boolean"
      ? data.downloadEligible
      : typeof data?.eligible === "boolean"
        ? data.eligible
        : typeof data?.canDownload === "boolean"
          ? data.canDownload
          : success;

  return {
    success,
    status: normalized,
    downloadEligible,
    paymentId:
      subscription?.transactionId ||
      data?.paymentId ||
      data?.payment?.id ||
      data?.record?.paymentId ||
      data?.paymentRecord?.paymentId ||
      null,
    subscription,
    raw: data,
  };
}

export function buildDownloadPurchaseContext({
  userName,
  fileName,
  format = "pdf",
  downloadLabel = "PDF",
  source = "resume-download",
} = {}) {
  const normalizedFormat = String(format || "pdf").toLowerCase();
  const safeUserName = String(userName || "").trim() || "guest";
  const safeFileName = String(fileName || "").trim() || "resume";
  const purchaseKey = [
    "resume-subscription",
    sanitizeSegment(safeUserName, "guest"),
  ].join(":");

  return {
    purchaseKey,
    userName: safeUserName,
    fileName: safeFileName,
    format: normalizedFormat,
    downloadLabel: String(downloadLabel || normalizedFormat).toUpperCase(),
    source,
  };
}

export function readStoredDownloadAccess(purchaseKey) {
  if (!purchaseKey) return null;

  const storeMap = readStoreMap();
  return storeMap[purchaseKey] || null;
}

export function clearStoredDownloadAccess(purchaseKey) {
  if (!purchaseKey) return;

  const storeMap = readStoreMap();
  if (!storeMap[purchaseKey]) return;

  delete storeMap[purchaseKey];
  writeStoreMap(storeMap);
}

export function persistSuccessfulPayment(context, details = {}) {
  if (!context?.purchaseKey) return null;

  const nextRecord = {
    ...context,
    status: "success",
    downloadEligible: details.subscription?.active !== false,
    paymentId:
      details.paymentId ||
      details.subscription?.transactionId ||
      details.raw?.paymentId ||
      null,
    paidAt:
      details.subscription?.subscriptionStartAt ||
      details.paidAt ||
      details.raw?.paidAt ||
      Date.now(),
    subscriptionStartAt: details.subscription?.subscriptionStartAt || null,
    subscriptionExpiresAt: details.subscription?.subscriptionExpiresAt || null,
    daysRemaining: details.subscription?.daysRemaining || 0,
    lastVerifiedAt: Date.now(),
    lastDownloadFailureAt: null,
    lastDownloadFailureReason: null,
    lastDownloadFailureMessage: null,
  };

  const storeMap = readStoreMap();
  storeMap[context.purchaseKey] = {
    ...(storeMap[context.purchaseKey] || {}),
    ...nextRecord,
  };
  writeStoreMap(storeMap);
  broadcastSubscriptionUpdate(details.subscription || storeMap[context.purchaseKey]);
  return storeMap[context.purchaseKey];
}

export function persistDownloadFailure(context, details = {}) {
  if (!context?.purchaseKey) return null;

  const storeMap = readStoreMap();
  const previousRecord = storeMap[context.purchaseKey] || {};
  storeMap[context.purchaseKey] = {
    ...previousRecord,
    ...context,
    status: previousRecord.status || "success",
    downloadEligible: true,
    lastVerifiedAt: previousRecord.lastVerifiedAt || Date.now(),
    lastDownloadFailureAt: Date.now(),
    lastDownloadFailureReason: details.reason || "unknown",
    lastDownloadFailureMessage:
      details.message || "Download failed due to a server issue. Please try again.",
    lastDownloadFailureStatusCode: details.statusCode || null,
  };
  writeStoreMap(storeMap);
  return storeMap[context.purchaseKey];
}

export async function fetchExistingSuccessfulPayment(context = {}) {
  if (!context.purchaseKey) {
    return {
      success: false,
      downloadEligible: false,
      status: "missing",
      raw: null,
    };
  }

  const payload = buildStatusPayload(context);
  const params = new URLSearchParams();

  Object.entries(payload).forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    }
  });

  const baseUrl = `${API.PAYMENT_STATUS}${params.toString() ? `?${params.toString()}` : ""}`;

  try {
    const getResponse = await authenticatedFetch(baseUrl, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    const getData = await parseStatusResponse(getResponse);
    return extractPaymentEligibility(getData);
  } catch (error) {
    if (error?.status && ![404, 405].includes(error.status)) {
      throw error;
    }
  }

  const postResponse = await authenticatedFetch(API.PAYMENT_STATUS, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  const postData = await parseStatusResponse(postResponse);
  return extractPaymentEligibility(postData);
}

export async function fetchSubscriptionStatus() {
  const response = await authenticatedFetch(API.SUBSCRIPTION_STATUS, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const data = await parseStatusResponse(response);
  return data.subscription || null;
}

export async function fetchPaymentHistory() {
  const response = await authenticatedFetch(API.PAYMENT_HISTORY, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const data = await parseStatusResponse(response);
  return Array.isArray(data.payments) ? data.payments : [];
}
